import { useLocalStore, useObserver } from 'mobx-react-lite';
import React, { HTMLAttributes } from 'react';
import SlidingPane from 'react-sliding-pane';
import 'react-sliding-pane/dist/react-sliding-pane.css';
import styled from 'styled-components/macro';
import { CartItemHelper } from '../models/CartItem';
import { useMst } from '../models/WithWineState';
import {
  CartItemOptionQueryStringModel,
  CartItemQueryStringModel,
} from '../types/CartItemQueryStringModel';
import { CartDispatchData } from '../types/EventDataProps';
import { TypeShippingRateType } from '../types/TypeShippingRateType';
import { base64ToBase64URL } from '../utils/base64ToBase64URL';
import {
  createProductData,
  dispatchCheckoutListData,
} from '../utils/dispatchHelper';
import removeDoubleSlashes from '../utils/removeDoubleSlashes';
import IconButton from './IconButton';
import ProductCardV2 from './ProductCardV2';

const UnstyledCartV2: React.FC<HTMLAttributes<HTMLDivElement>> = ({
  className,
}) => {
  const stateTree = useMst();

  const state = useLocalStore(() => ({
    onStateChange(e: React.ChangeEvent<HTMLSelectElement>) {
      stateTree.session?.setDeliveryState(e.target.value);
      stateTree.session?.calculateCart();
    },

    get isAuth() {
      return stateTree.session?.authenticated ?? false;
    },

    get dispatchEventData() {
      const cartItems: CartDispatchData[] = [];

      stateTree.session?.cart.forEach((cartItem) => {
        const cart = {
          product: createProductData(cartItem.product),
          quantity: cartItem.quantity,
        };
        cartItems.push(cart);
      });

      return {
        user: {
          id: stateTree.session?.user?.id,
          name: stateTree.session?.user?.name,
          sessionId: stateTree.session?.authenticated
            ? stateTree.session?.user?.id
            : stateTree.session?.id,
        },
        cart: cartItems,
      };
    },

    get state() {
      return stateTree.session?.deliveryFields?.state ?? '';
    },

    get image() {
      return removeDoubleSlashes(
        `${(globalThis as any)['BASE_URL']}/assets/empty-cart.png`,
      );
    },

    get shippingFee() {
      const fee = stateTree.session?.shippingFee;
      return typeof fee === 'number' ? +fee.toFixed(2) : undefined;
    },

    get isOpen() {
      if (stateTree.widgets.cart.openVersion2) {
        this.items.forEach((item) => {
          const parent = document.getElementById(item.id.toString());
          if (parent) {
            const btn = parent.querySelector('#btn-close') as HTMLElement;
            btn.click();
          }
        });
      }
      return stateTree.widgets.cart.openVersion2;
    },

    get items() {
      return stateTree.session?.items || [];
    },

    get errorMessage() {
      return stateTree.session?.calculatePriceError;
    },

    get totalPrice() {
      if (stateTree.session?.calculatedCartPrice) {
        return +stateTree.session?.calculatedCartPrice;
      } else {
        return (
          +state.items.reduce((sum: number, cartItem) => {
            return sum + (cartItem.price || 0);
          }, 0) + (state.shippingFee ?? 0)
        );
      }
    },

    get totalTaxAdded() {
      return stateTree.session?.totalTaxAdded ?? 0;
    },

    get addedTaxCodeDescription() {
      return stateTree.session?.addedTaxCodeDescription || 'Sales Tax';
    },

    get totalItemCount() {
      return stateTree.session?.getCartQuantityByProductGroup(null) ?? 0;
    },

    get allItemsStockAvailable() {
      return state.items.every((item) => item.stockAvailable);
    },

    get ableToCheckout() {
      return (
        stateTree.session?.calculatePriceState === 'idle' &&
        stateTree.session?.calculatedCartPrice &&
        !stateTree.session?.calculatePriceError &&
        stateTree.session?.getCartQuantityByProductGroup(null) > 0 &&
        state.allItemsStockAvailable
      );
    },

    get isStateShipping() {
      const brand = Array.from(stateTree.data.brands.values())[0];
      return brand.shippingRateType === TypeShippingRateType.ByState;
    },

    hide() {
      stateTree.widgets.cart.hide();
    },

    checkout() {
      if (!state.ableToCheckout) {
        return;
      }

      dispatchCheckoutListData(state.dispatchEventData);

      if (state.items) {
        const queryStringCartModel = state.items.map((item) => {
          const cartItem: CartItemQueryStringModel = {
            P: item.id,
            Q: item.value,
          };
          if (item.selectedOptions && item.selectedOptions.length) {
            const numberOfOptions = CartItemHelper.getOptionQuantity(
              item.minimumUnitPurchase,
              item.value,
            );
            cartItem.O = item.selectedOptions?.map((so) => {
              const option: CartItemOptionQueryStringModel = {
                Q: numberOfOptions,
                I: 0,
              };
              if (so.productId) {
                option.P = so.productId;
              } else if (so.name) {
                option.N = so.name;
              }

              return option;
            });
          }
          return cartItem;
        });
        const queryStringCartJson = JSON.stringify(queryStringCartModel);
        const base64EncodedData = btoa(queryStringCartJson);
        const cartUrlParam = base64ToBase64URL(base64EncodedData);
        const sessionKey = state.isAuth
          ? ``
          : `&sessionKey=${stateTree.session?.id}`;
        window.location.href = `/user/checkout?state=${state.state}&cart=${cartUrlParam}${sessionKey}`;
      }
    },
  }));

  return useObserver(() => (
    <SlidingPane
      className={'ww-widget ww-cart ' + className}
      shouldCloseOnEsc={true}
      closeIcon={
        <IconButton>
          <i className="icon-cancel" />
        </IconButton>
      }
      isOpen={state.isOpen}
      title="Your Cart"
      from="right"
      onRequestClose={state.hide}
      width={window.innerWidth < 600 ? '100%' : '570px'}
    >
      {state.items.length > 0 ? (
        <div className="absolute inset-0 text-center">
          <div className="cart-items">
            {state.items.map((val) => (
              <ProductCardV2
                product={{ id: val.id.toString() }}
                cartV2Enabled={true}
              />
            ))}
          </div>
          <div className="footer">
            {state.errorMessage !== '' ||
            state.errorMessage !== undefined ||
            state.errorMessage !== null ? (
              <div className="callout">{state.errorMessage}</div>
            ) : (
              <></>
            )}

            <div className="relative shipping-total">
              <div style={{ display: 'flex', width: '100%' }}>
                <div
                  style={{ flex: '1' }}
                  className="text-base font-medium font-bold text-left"
                >
                  Shipping
                </div>
                <div className="text-base font-medium font-light text-right">
                  {typeof state.shippingFee === 'number'
                    ? `$${state.shippingFee?.toFixed(2)}`
                    : '$-.--'}
                </div>
              </div>

              {!state.errorMessage &&
                state.totalItemCount > 0 &&
                state.totalTaxAdded !== 0 && (
                  <div style={{ display: 'flex', width: '100%' }}>
                    <div
                      style={{ flex: '1' }}
                      className="text-base font-medium font-bold text-left"
                    >
                      {state.addedTaxCodeDescription}
                    </div>
                    <div className="text-base font-medium font-light text-right">{`$${state.totalTaxAdded.toFixed(
                      2,
                    )}`}</div>
                  </div>
                )}

              <div style={{ display: 'flex', width: '100%' }}>
                <div
                  style={{ flex: '1' }}
                  className="text-base font-bold font-light text-left"
                >
                  Total
                </div>
                <div className="text-base font-medium font-light text-right">
                  ${state.totalPrice.toFixed(2)}
                  {typeof state.shippingFee === 'number' ? (
                    <></>
                  ) : (
                    <span className="amount-addon"> Plus Shipping</span>
                  )}
                </div>
              </div>
            </div>
            <button
              className="bg-black 
                              border 
                              border-transparent 
                              rounded-none 
                              rounded-3xl 
                              text-sm font-medium 
                              uppercase 
                              text-white
                              check-out-button"
              onClick={state.checkout}
              disabled={!state.ableToCheckout}
            >
              check out
            </button>
          </div>
        </div>
      ) : (
        <div className="empty-cart-container absolute inset-0 text-center">
          <img
            className="empty-cart-img"
            src={state.image}
            alt={'empty-cart'}
          />
          <div className="text-container">
            <span className="text-xl font-bold line-height-30 block text">
              Uh oh, your cart is empty!
            </span>
            <span className="text-base font-medium font-light text">
              Let us help with that…
            </span>
          </div>
          <button
            className=" bg-black 
                                border 
                                border-transparent 
                                rounded-none 
                                rounded-3xl 
                                text-sm font-medium 
                                uppercase 
                                text-white
                                shop-now-button"
            onClick={state.hide}
          >
            shop now
          </button>
        </div>
      )}
    </SlidingPane>
  ));
};

const CartV2 = styled(UnstyledCartV2)`
  .empty-cart-container {
    margin: auto;
    height: fit-content;
  }

  .shop-now-button {
    width: 400px;
    height: 50px;
    fontSize: 12px;
    margin: auto;
  }

  .empty-cart-img {
    width: 250px;
    height: 250px;
    margin: auto;
    backgroundImage: url(../assets/box.png);
  };

  .text-container {
    margin: 30px 0 50px 0;
  }

  .shipping-total {
    border-top: 1px solid;
    border-bottom: 1px solid;
    border-color: #efefef;
    padding: 15px;
    margin: 20px 65px;
  }

  .check-out-button {
    width: 400px;
    height: 50px;
    fontSize: 12pt;
    margin: auto;
    backgroundColor: darkgray;
  }

  .callout {
    margin: 15px 65px 20px 50px;
    position: relative;
    width: auto;
    padding: 18px;
    background-color: rgb(194, 155, 71);
    border-radius: 1.231em;
    color: white;
    font-size: 14px;
  }

  .callout:before {
    content: "";
    position: absolute;
    width: 0px;
    height: 0px;
    right: 20px;
    bottom: -35px;
    border-width: 20px 20px 25px;
    border-style: solid;
    border-color: rgb(194, 155, 71) transparent transparent;
    border-image: initial;
  }

  .cart-items {
    height: 70%;
    margin: 20px 0; 
    minHeight: 500px;
    overflow: auto;
  }

  .footer {
    position: absolute;
    bottom: 15px;
    margin: auto;
    right: 0;
    left: 0;
  }

  .ww-product-card {
    position: relative;
    display: flex;
    flex-direction: column;
    background-color: var(--white);
    border-radius: 1.231em;
    box-shadow: 0 0.5em 1em rgba(0, 0, 0, 0.1);
    /* width: 31.923em; */
    min-width: 23.538em;
    color: var(--black);
    margin: 30px 55px;
  
    &.store {
      flex: none;
      flex-basis: 32%;
    }
  
    .spacer {
      flex: auto;
    }
  
    .ww-product-card-header {
      position: absolute;
      top: 0;
      right: 0;
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      justify-content: flex-end;
      padding: 0.769em;
      .ww-icon-button {
        font-size: 16px;
        border: unset;
        color: black;
      }
    }
  
    .ww-product-card-content {
      display: flex;
      padding: 20px 0px;
      border-bottom: 1px solid #e8e8e8;
  
      .ww-product-image {
        flex: none;
        width: 6.846em;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        overflow: hidden;
        img {
          height: 70px;
          flex: none;
        }
      }
  
      .ww-product-content {
        min-height: 3em;
        display: flex;
        flex-direction: column;
        padding-right: 1.538em;
  
        .ww-product-winery {
          margin-right: 6.154em;
          font-size: 16px;
          text-align: left;
        }
  
        .ww-product-name {
          margin: 0;
          margin-right: 4em;
          font-size: 16px;
          font-weight: bold;
          text-align: left;
  
          a {
            color: inherit;
            text-decoration: none;
          }
        }
  
        .ww-product-location {
          font-size: 16px;
          text-align: left;
          color: var(--gray-4);
        }
      }
    }
  
    &.store .ww-product-card-content {
      flex: auto;
      flex-direction: column-reverse;
      padding: 1.538em;
  
      .ww-product-image {
        flex: auto;
        margin-top: 1.538em;
        width: 100%;
        align-items: flex-end;
        justify-content: center;
        img {
          height: 15.385em;
        }
      }
    }
  
    .ww-product-card-footer {
      border-top: 1px solid var(--gray-2);
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 10px;
  
      .price-info,
      .quantity {
        font-size: 18px;
        font-weight: bold;
        font-family: inherit;
        flex: none;
        margin-left: 0.556em;
      }
  
      .divider {
        font-size: 1.538em;
        color: var(--gray-5);
        margin: 0 0.2em;
        flex: none;
      }
  
      .variation-select {
        appearance: none;
        padding: 0 0.25em;
        flex: none;
        border: none;
        height: 1.538em;
        display: flex;
        align-items: center;
        font-family: inherit;
        background-color: var(--gray-2);
        border-radius: 0.154em;
        font-size: 16px;
      }
  
      .ww-icon-button {
        flex: none;
        margin-left: 0.769em;
        background-color: black;
        color: white;
        font-size: 12px;
      }
    }
`;

export default CartV2;
